import React, { useEffect, useRef } from 'react';

/**
 * Component to highlight matched search query text.
 */
export function HighlightText({ text, query }) {
  if (!query || !text) return <span>{text}</span>;
  
  // Escape special regex characters
  const escapedQuery = String(query).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${escapQuery})`, 'gi'));
  
  return (
    <span>
      {parts.map((part, i) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark 
            key={i} 
            className="search-highlight" 
            style={{ 
              backgroundColor: 'rgba(108, 99, 255, 0.16)', 
              color: 'var(--brand-700)', 
              padding: '1px 2px', 
              borderRadius: '3px', 
              fontWeight: 600 
            }}
          >
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </span>
  );
}

/**
 * Dropdown list overlay for search results.
 */
export default function SearchResultsDropdown({
  results = {}, // E.g. { employees: [...], departments: [...] } or flat array [...]
  query = '',
  onSelect,
  selectedIndex = -1,
  loading = false,
  visible = false,
  onClose,
  style = {}
}) {
  const containerRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  const isGrouped = !Array.isArray(results);
  
  // Flatten items to map flat index to grouped item
  const flatItems = [];
  if (isGrouped) {
    Object.entries(results).forEach(([moduleName, items]) => {
      if (items && items.length > 0) {
        items.forEach(item => {
          flatItems.push({ ...item, group: moduleName });
        });
      }
    });
  } else {
    flatItems.push(...(results || []));
  }

  const hasResults = flatItems.length > 0;

  return (
    <div 
      ref={containerRef}
      className="search-results-dropdown card"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 6,
        maxHeight: 380,
        overflowY: 'auto',
        zIndex: 99,
        background: '#FFFFFF',
        boxShadow: 'var(--shadow-lg)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: '12px 14px',
        animation: 'slideUp 0.15s var(--ease)',
        ...style
      }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: 13.5 }}>
          Searching...
        </div>
      )}

      {!loading && !hasResults && query.trim() && (
        <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: 13.5 }}>
          No results found for "{query}"
        </div>
      )}

      {!loading && !hasResults && !query.trim() && (
        <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: 13 }}>
          Type to search...
        </div>
      )}

      {!loading && hasResults && (
        <>
          {isGrouped ? (
            Object.entries(results).map(([moduleName, items]) => {
              if (!items || items.length === 0) return null;
              return (
                <div key={moduleName} style={{ marginBottom: 12 }}>
                  <div style={{ 
                    fontSize: 10.5, 
                    fontWeight: 750, 
                    textTransform: 'uppercase', 
                    color: 'var(--brand-600)', 
                    letterSpacing: '0.6px', 
                    marginBottom: 6, 
                    borderBottom: '1px solid #F3F4F6', 
                    paddingBottom: 4 
                  }}>
                    {moduleName.replace('_', ' ')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {items.map(item => {
                      const flatIndex = flatItems.findIndex(f => f.id === item.id && f.group === moduleName);
                      const isSelected = flatIndex === selectedIndex;
                      return (
                        <div
                          key={item.id}
                          onClick={() => onSelect?.(item)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            background: isSelected ? 'var(--brand-50)' : 'transparent',
                            borderLeft: isSelected ? '3px solid var(--brand-500)' : '3px solid transparent',
                            transition: 'all 0.15s'
                          }}
                          onMouseOver={e => {
                            if (!isSelected) e.currentTarget.style.background = 'var(--surface-2)';
                          }}
                          onMouseOut={e => {
                            if (!isSelected) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: isSelected ? 'var(--brand-900)' : 'var(--text-primary)' }}>
                            <HighlightText text={item.title} query={query} />
                          </div>
                          {item.subtitle && (
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                              <HighlightText text={item.subtitle} query={query} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {flatItems.map((item, flatIndex) => {
                const isSelected = flatIndex === selectedIndex;
                return (
                  <div
                    key={item.id}
                    onClick={() => onSelect?.(item)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      background: isSelected ? 'var(--brand-50)' : 'transparent',
                      borderLeft: isSelected ? '3px solid var(--brand-500)' : '3px solid transparent',
                      transition: 'all 0.15s'
                    }}
                    onMouseOver={e => {
                      if (!isSelected) e.currentTarget.style.background = 'var(--surface-2)';
                    }}
                    onMouseOut={e => {
                      if (!isSelected) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: isSelected ? 'var(--brand-900)' : 'var(--text-primary)' }}>
                      <HighlightText text={item.title} query={query} />
                    </div>
                    {item.subtitle && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                        <HighlightText text={item.subtitle} query={query} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
